/**
 * Admin API Service
 * Hooks for admin console endpoints (client management, settings, usage, enums)
 */
import { useCallback, useMemo } from "react";
import { useApi } from "@/hooks/use-api";
import type {
    AcomDefaultsDto,
    AddOrganizationDocumentTypeRequestDto,
    AddOrganizationDocumentTypeResponseDto,
    // Audit Events (Admin)
    AdminAuditEventListParams,
    AdminAuditEventListResponse,
    // Bridge Registrations
    AdminBridgeRegistrationListResponse,
    AdminClientEntitlementListResponse,
    AdminClientListParams,
    AdminClientListResponse,
    AdminClientPlanListResponse,
    // Email Sources
    AdminEmailSourceListResponse,
    AdminEnumListResponse,
    // Exceptions (Admin)
    AdminExceptionListParams,
    AdminExceptionListResponse,
    AdminFieldDefinitionListResponse,
    AdminFieldListParams,
    AdminGlobalBridgeListResponse,
    // Jobs (Admin)
    AdminJobListParams,
    AdminJobListResponse,
    AdminOrganizationDocumentTypeListResponse,
    AdminOrganizationFileTypeListResponse,
    AdminOrganizationListResponse,
    // Prompt Templates
    AdminPromptTemplateListResponse,
    AdminUserListParams,
    AdminUserListResponse,
    AdminWorkflowTemplateListResponse,
    AuditEventAdminDetailDto,
    BridgeRegistrationAdminListItemDto,
    BulkRequeueRequestDto,
    BulkRequeueResponseDto,
    BulkUpdateUserRestrictionResultDto,
    ClientAdminDetailDto,
    ClientEntitlementAdminListItemDto,
    ClientPlanAdminListItemDto,
    ClientUsageAdminDto,
    CreateBridgeRegistrationRequestDto,
    CreateClientEntitlementRequestDto,
    CreateClientPlanRequestDto,
    CreateClientRequestDto,
    CreateCustomFieldDefinitionRequestDto,
    CreateDocumentTypeRequestDto,
    CreateLineItemTypeRequestDto,
    CreateMailboxConfigurationDto,
    CreateOrganizationRequestDto,
    CreatePlanRequestDto,
    CreatePromptTemplateRequestDto,
    CreateRuleRequestDto,
    CreateRuleSetRequestDto,
    CreateWorkflowStepRequestDto,
    CreateWorkflowTemplateRequestDto,
    CustomFieldDefinitionAdminDetailDto,
    CustomFieldDefinitionAdminListItemDto,
    DocumentTypeAdminListItemDto,
    EntitlementAdminListItemDto,
    ExceptionAdminDetailDto,
    JobAdminDetailDto,
    LineItemTypeAdminListItemDto,
    MailboxConfigurationDto,
    MailboxStatusDto,
    MailboxWorkerKeyGeneratedDto,
    MailboxWorkerKeyMetadataDto,
    MonitoringDashboardDto,
    OrgDeactivatePreCheckResponseDto,
    OrganizationAdminListItemDto,
    OrganizationDocumentTypeDto,
    OrganizationFileTypeDto,
    PlanAdminListItemDto,
    PlanEntitlementAdminListItemDto,
    PromptTemplateAdminDetailDto,
    RegenerateBridgeSecretResponseDto,
    ReorderWorkflowStepsRequestDto,
    ResolveExceptionRequestDto,
    RuleAdminListItemDto,
    RuleSetAdminDetailDto,
    SetOrganizationFileTypeRequestDto,
    StepTypeAdminListItemDto,
    SuppressExceptionRequestDto,
    UpdateBridgeRegistrationConfigRequestDto,
    UpdateClientEntitlementRequestDto,
    UpdateClientPlanRequestDto,
    UpdateClientRequestDto,
    UpdateClientSettingsRequestDto,
    UpdateCustomFieldDefinitionRequestDto,
    UpdateDocumentTypeRequestDto,
    UpdateLineItemTypeRequestDto,
    UpdateMailboxConfigurationDto,
    UpdateOrganizationDocumentTypeRequestDto,
    UpdateOrganizationRequestDto,
    UpdatePlanEntitlementRequestDto,
    UpdatePlanRequestDto,
    UpdatePromptTemplateRequestDto,
    UpdateRuleRequestDto,
    UpdateRuleSetRequestDto,
    UpdateUserRequestDto,
    UpdateWorkflowStepRequestDto,
    UpdateWorkflowTemplateRequestDto,
    UserAdminListItemDto,
    WorkflowStepAdminListItemDto,
    WorkflowTemplateAdminDetailDto,
} from "@/types/api/admin";

const ADMIN_BASE = "/mgmt";

/**
 * Hook providing admin API methods for client management
 */
export function useAdminApi() {
    const api = useApi();

    // =========================================================================
    // CLIENTS
    // =========================================================================

    /** List clients with optional filtering and pagination */
    const listClients = useCallback(
        async (params?: AdminClientListParams): Promise<AdminClientListResponse> => {
            const searchParams = new URLSearchParams();
            if (params?.isActive !== undefined) searchParams.set("isActive", String(params.isActive));
            if (params?.search) searchParams.set("search", params.search);
            if (params?.pageSize !== undefined) searchParams.set("pageSize", String(params.pageSize));
            if (params?.skip !== undefined) searchParams.set("skip", String(params.skip));

            const query = searchParams.toString();
            const endpoint = query ? `${ADMIN_BASE}/clients?${query}` : `${ADMIN_BASE}/clients`;
            return api.get<AdminClientListResponse>(endpoint);
        },
        [api],
    );

    /** Get a single client by ID */
    const getClient = useCallback(
        async (clientId: number): Promise<ClientAdminDetailDto> => {
            return api.get<ClientAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}`);
        },
        [api],
    );

    /** Create a new client */
    const createClient = useCallback(
        async (dto: CreateClientRequestDto): Promise<ClientAdminDetailDto> => {
            return api.post<ClientAdminDetailDto>(`${ADMIN_BASE}/clients`, dto);
        },
        [api],
    );

    /** Update an existing client */
    const updateClient = useCallback(
        async (clientId: number, dto: UpdateClientRequestDto): Promise<ClientAdminDetailDto> => {
            return api.put<ClientAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}`, dto);
        },
        [api],
    );

    // =========================================================================
    // SETTINGS
    // =========================================================================

    /** Get client settings */
    const getClientSettings = useCallback(
        async (clientId: number): Promise<Record<string, unknown>> => {
            return api.get<Record<string, unknown>>(`${ADMIN_BASE}/clients/${clientId}/settings`);
        },
        [api],
    );

    /** Update client settings with merge-patch semantics */
    const updateClientSettings = useCallback(
        async (clientId: number, dto: UpdateClientSettingsRequestDto): Promise<Record<string, unknown>> => {
            return api.patch<Record<string, unknown>>(`${ADMIN_BASE}/clients/${clientId}/settings`, dto);
        },
        [api],
    );

    // =========================================================================
    // USAGE
    // =========================================================================

    /** Get client usage for the current billing period */
    const getClientUsage = useCallback(
        async (clientId: number): Promise<ClientUsageAdminDto> => {
            return api.get<ClientUsageAdminDto>(`${ADMIN_BASE}/clients/${clientId}/usage`);
        },
        [api],
    );

    // =========================================================================
    // ENUMS
    // =========================================================================

    /** Get all admin-relevant enum metadata */
    const getEnumMetadata = useCallback(async (): Promise<AdminEnumListResponse> => {
        return api.get<AdminEnumListResponse>(`${ADMIN_BASE}/enums`);
    }, [api]);

    // =========================================================================
    // PLANS
    // =========================================================================

    /** List all plans (for dropdown) */
    const listPlans = useCallback(async (): Promise<PlanAdminListItemDto[]> => {
        const res = await api.get<{ data: PlanAdminListItemDto[] }>(`${ADMIN_BASE}/plans`);
        return res.data;
    }, [api]);

    /** List client plan history */
    const listClientPlans = useCallback(
        async (clientId: number, pageSize?: number, skip?: number): Promise<AdminClientPlanListResponse> => {
            const params = new URLSearchParams();
            if (pageSize !== undefined) params.set("pageSize", String(pageSize));
            if (skip !== undefined) params.set("skip", String(skip));
            const query = params.toString();
            return api.get<AdminClientPlanListResponse>(`${ADMIN_BASE}/clients/${clientId}/plans${query ? `?${query}` : ""}`);
        },
        [api],
    );

    /** Create a client plan subscription */
    const createClientPlan = useCallback(
        async (clientId: number, dto: CreateClientPlanRequestDto): Promise<ClientPlanAdminListItemDto> => {
            return api.post<ClientPlanAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/plans`, dto);
        },
        [api],
    );

    /** Update a client plan subscription */
    const updateClientPlan = useCallback(
        async (clientId: number, planId: number, dto: UpdateClientPlanRequestDto): Promise<ClientPlanAdminListItemDto> => {
            return api.put<ClientPlanAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/plans/${planId}`, dto);
        },
        [api],
    );

    /** Create a new plan */
    const createPlan = useCallback(
        async (dto: CreatePlanRequestDto): Promise<PlanAdminListItemDto> => {
            return api.post<PlanAdminListItemDto>(`${ADMIN_BASE}/plans`, dto);
        },
        [api],
    );

    /** Update an existing plan */
    const updatePlan = useCallback(
        async (id: number, dto: UpdatePlanRequestDto): Promise<PlanAdminListItemDto> => {
            return api.patch<PlanAdminListItemDto>(`${ADMIN_BASE}/plans/${id}`, dto);
        },
        [api],
    );

    /** Get entitlements for a plan */
    const getPlanEntitlements = useCallback(
        async (planId: number): Promise<PlanEntitlementAdminListItemDto[]> => {
            const res = await api.get<{ data: PlanEntitlementAdminListItemDto[] }>(`${ADMIN_BASE}/plans/${planId}/entitlements`);
            return res.data;
        },
        [api],
    );

    /** Update a plan entitlement (upsert — enabled state and limit value) */
    const updatePlanEntitlement = useCallback(
        async (planId: number, entitlementId: number, dto: UpdatePlanEntitlementRequestDto): Promise<PlanEntitlementAdminListItemDto> => {
            return api.patch<PlanEntitlementAdminListItemDto>(`${ADMIN_BASE}/plans/${planId}/entitlements/${entitlementId}`, dto);
        },
        [api],
    );

    /** List entitlements from active plan */
    const listClientPlanEntitlements = useCallback(
        async (clientId: number): Promise<PlanEntitlementAdminListItemDto[]> => {
            const res = await api.get<{ data: PlanEntitlementAdminListItemDto[] }>(`${ADMIN_BASE}/clients/${clientId}/plan-entitlements`);
            return res.data;
        },
        [api],
    );

    // =========================================================================
    // ENTITLEMENTS
    // =========================================================================

    /** List all entitlements (for dropdown) */
    const listEntitlements = useCallback(async (): Promise<EntitlementAdminListItemDto[]> => {
        const res = await api.get<{ data: EntitlementAdminListItemDto[] }>(`${ADMIN_BASE}/entitlements`);
        return res.data;
    }, [api]);

    /** List client entitlement add-ons */
    const listClientEntitlements = useCallback(
        async (clientId: number, pageSize?: number, skip?: number): Promise<AdminClientEntitlementListResponse> => {
            const params = new URLSearchParams();
            if (pageSize !== undefined) params.set("pageSize", String(pageSize));
            if (skip !== undefined) params.set("skip", String(skip));
            const query = params.toString();
            return api.get<AdminClientEntitlementListResponse>(`${ADMIN_BASE}/clients/${clientId}/entitlements${query ? `?${query}` : ""}`);
        },
        [api],
    );

    /** Create a client entitlement add-on */
    const createClientEntitlement = useCallback(
        async (clientId: number, dto: CreateClientEntitlementRequestDto): Promise<ClientEntitlementAdminListItemDto> => {
            return api.post<ClientEntitlementAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/entitlements`, dto);
        },
        [api],
    );

    /** Update a client entitlement add-on */
    const updateClientEntitlement = useCallback(
        async (clientId: number, id: number, dto: UpdateClientEntitlementRequestDto): Promise<ClientEntitlementAdminListItemDto> => {
            return api.put<ClientEntitlementAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/entitlements/${id}`, dto);
        },
        [api],
    );

    /** Delete a client entitlement add-on */
    const deleteClientEntitlement = useCallback(
        async (clientId: number, id: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/entitlements/${id}`);
        },
        [api],
    );

    // =========================================================================
    // CUSTOM FIELD DEFINITIONS
    // =========================================================================

    /** List field definitions for an organization */
    const listFieldDefinitions = useCallback(
        async (clientId: number, organizationId: number, params?: AdminFieldListParams): Promise<AdminFieldDefinitionListResponse> => {
            const searchParams = new URLSearchParams();
            if (params?.entityType) searchParams.set("entityType", params.entityType);
            if (params?.entityId !== undefined) searchParams.set("entityId", String(params.entityId));
            if (params?.pageSize !== undefined) searchParams.set("pageSize", String(params.pageSize));
            if (params?.skip !== undefined) searchParams.set("skip", String(params.skip));
            const query = searchParams.toString();
            return api.get<AdminFieldDefinitionListResponse>(
                `${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/fields${query ? `?${query}` : ""}`,
            );
        },
        [api],
    );

    /** Get a single field definition detail */
    const getFieldDefinition = useCallback(
        async (clientId: number, organizationId: number, fieldId: number): Promise<CustomFieldDefinitionAdminDetailDto> => {
            return api.get<CustomFieldDefinitionAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/fields/${fieldId}`);
        },
        [api],
    );

    /** Create a custom field definition */
    const createFieldDefinition = useCallback(
        async (clientId: number, organizationId: number, dto: CreateCustomFieldDefinitionRequestDto): Promise<CustomFieldDefinitionAdminListItemDto> => {
            return api.post<CustomFieldDefinitionAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/fields`, dto);
        },
        [api],
    );

    /** Update a custom field definition */
    const updateFieldDefinition = useCallback(
        async (
            clientId: number,
            organizationId: number,
            fieldId: number,
            dto: UpdateCustomFieldDefinitionRequestDto,
        ): Promise<CustomFieldDefinitionAdminListItemDto> => {
            return api.put<CustomFieldDefinitionAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/fields/${fieldId}`, dto);
        },
        [api],
    );

    /** Delete a custom field definition */
    const deleteFieldDefinition = useCallback(
        async (clientId: number, organizationId: number, fieldId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/fields/${fieldId}`);
        },
        [api],
    );

    /** Reorder field definitions (batch update display order) */
    const reorderFieldDefinitions = useCallback(
        async (clientId: number, organizationId: number, items: { id: number; displayOrder: number }[]): Promise<void> => {
            await api.put(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/fields/reorder`, { items });
        },
        [api],
    );

    // =========================================================================
    // ACOM DEFAULTS
    // =========================================================================

    /** Get ACOM platform defaults to pre-populate new client creation */
    const getAcomDefaults = useCallback(async (): Promise<AcomDefaultsDto> => {
        return api.get<AcomDefaultsDto>(`${ADMIN_BASE}/clients/acom-defaults`);
    }, [api]);

    // =========================================================================
    // REFERENCE DATA
    // =========================================================================

    /** List all document types (for management) */
    const listDocumentTypes = useCallback(async (): Promise<DocumentTypeAdminListItemDto[]> => {
        const res = await api.get<{ data: DocumentTypeAdminListItemDto[] }>(`${ADMIN_BASE}/document-types`);
        return res.data;
    }, [api]);

    /** Create a new document type */
    const createDocumentType = useCallback(
        async (dto: CreateDocumentTypeRequestDto): Promise<DocumentTypeAdminListItemDto> => {
            return api.post<DocumentTypeAdminListItemDto>(`${ADMIN_BASE}/document-types`, dto);
        },
        [api],
    );

    /** Update an existing document type */
    const updateDocumentType = useCallback(
        async (id: number, dto: UpdateDocumentTypeRequestDto): Promise<DocumentTypeAdminListItemDto> => {
            return api.patch<DocumentTypeAdminListItemDto>(`${ADMIN_BASE}/document-types/${id}`, dto);
        },
        [api],
    );

    /** Delete a document type */
    const deleteDocumentType = useCallback(
        async (id: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/document-types/${id}`);
        },
        [api],
    );

    /** List all line item types (for dropdowns and management) */
    const listLineItemTypes = useCallback(async (): Promise<LineItemTypeAdminListItemDto[]> => {
        const res = await api.get<{ data: LineItemTypeAdminListItemDto[] }>(`${ADMIN_BASE}/line-item-types`);
        return res.data;
    }, [api]);

    /** Create a new line item type */
    const createLineItemType = useCallback(
        async (dto: CreateLineItemTypeRequestDto): Promise<LineItemTypeAdminListItemDto> => {
            return api.post<LineItemTypeAdminListItemDto>(`${ADMIN_BASE}/line-item-types`, dto);
        },
        [api],
    );

    /** Update an existing line item type */
    const updateLineItemType = useCallback(
        async (id: number, dto: UpdateLineItemTypeRequestDto): Promise<LineItemTypeAdminListItemDto> => {
            return api.patch<LineItemTypeAdminListItemDto>(`${ADMIN_BASE}/line-item-types/${id}`, dto);
        },
        [api],
    );

    // =========================================================================
    // STEP TYPES
    // =========================================================================

    /** List all step types (for dropdowns) */
    const listStepTypes = useCallback(async (): Promise<StepTypeAdminListItemDto[]> => {
        const res = await api.get<{ data: StepTypeAdminListItemDto[] }>(`${ADMIN_BASE}/step-types`);
        return res.data;
    }, [api]);

    // =========================================================================
    // WORKFLOW TEMPLATES
    // =========================================================================

    /** List workflow templates for a client */
    const listWorkflowTemplates = useCallback(
        async (clientId: number, pageSize?: number, skip?: number): Promise<AdminWorkflowTemplateListResponse> => {
            const params = new URLSearchParams();
            if (pageSize !== undefined) params.set("pageSize", String(pageSize));
            if (skip !== undefined) params.set("skip", String(skip));
            const query = params.toString();
            return api.get<AdminWorkflowTemplateListResponse>(`${ADMIN_BASE}/clients/${clientId}/workflows${query ? `?${query}` : ""}`);
        },
        [api],
    );

    /** Get a single workflow template with steps */
    const getWorkflowTemplate = useCallback(
        async (clientId: number, templateId: number): Promise<WorkflowTemplateAdminDetailDto> => {
            return api.get<WorkflowTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}`);
        },
        [api],
    );

    /** Create a workflow template */
    const createWorkflowTemplate = useCallback(
        async (clientId: number, dto: CreateWorkflowTemplateRequestDto): Promise<WorkflowTemplateAdminDetailDto> => {
            return api.post<WorkflowTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/workflows`, dto);
        },
        [api],
    );

    /** Update a workflow template */
    const updateWorkflowTemplate = useCallback(
        async (clientId: number, templateId: number, dto: UpdateWorkflowTemplateRequestDto): Promise<WorkflowTemplateAdminDetailDto> => {
            return api.put<WorkflowTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}`, dto);
        },
        [api],
    );

    /** Delete a workflow template */
    const deleteWorkflowTemplate = useCallback(
        async (clientId: number, templateId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}`);
        },
        [api],
    );

    // =========================================================================
    // WORKFLOW STEPS
    // =========================================================================

    /** Create a workflow step */
    const createWorkflowStep = useCallback(
        async (clientId: number, templateId: number, dto: CreateWorkflowStepRequestDto): Promise<WorkflowStepAdminListItemDto> => {
            return api.post<WorkflowStepAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps`, dto);
        },
        [api],
    );

    /** Update a workflow step */
    const updateWorkflowStep = useCallback(
        async (clientId: number, templateId: number, stepId: number, dto: UpdateWorkflowStepRequestDto): Promise<WorkflowStepAdminListItemDto> => {
            return api.put<WorkflowStepAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}`, dto);
        },
        [api],
    );

    /** Delete a workflow step */
    const deleteWorkflowStep = useCallback(
        async (clientId: number, templateId: number, stepId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}`);
        },
        [api],
    );

    /** Reorder workflow steps */
    const reorderWorkflowSteps = useCallback(
        async (clientId: number, templateId: number, dto: ReorderWorkflowStepsRequestDto): Promise<WorkflowTemplateAdminDetailDto> => {
            return api.put<WorkflowTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/reorder`, dto);
        },
        [api],
    );

    // =========================================================================
    // RULE SETS
    // =========================================================================

    /** List rule sets for a workflow step */
    const listStepRuleSets = useCallback(
        async (clientId: number, templateId: number, stepId: number): Promise<RuleSetAdminDetailDto[]> => {
            const res = await api.get<{ data: RuleSetAdminDetailDto[] }>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets`);
            return res.data;
        },
        [api],
    );

    /** Create a rule set */
    const createRuleSet = useCallback(
        async (clientId: number, templateId: number, stepId: number, dto: CreateRuleSetRequestDto): Promise<RuleSetAdminDetailDto> => {
            return api.post<RuleSetAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets`, dto);
        },
        [api],
    );

    /** Update a rule set */
    const updateRuleSet = useCallback(
        async (clientId: number, templateId: number, stepId: number, ruleSetId: number, dto: UpdateRuleSetRequestDto): Promise<RuleSetAdminDetailDto> => {
            return api.put<RuleSetAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets/${ruleSetId}`, dto);
        },
        [api],
    );

    /** Delete a rule set */
    const deleteRuleSet = useCallback(
        async (clientId: number, templateId: number, stepId: number, ruleSetId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets/${ruleSetId}`);
        },
        [api],
    );

    // =========================================================================
    // RULES
    // =========================================================================

    /** Create a rule */
    const createRule = useCallback(
        async (clientId: number, templateId: number, stepId: number, ruleSetId: number, dto: CreateRuleRequestDto): Promise<RuleAdminListItemDto> => {
            return api.post<RuleAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets/${ruleSetId}/rules`, dto);
        },
        [api],
    );

    /** Update a rule */
    const updateRule = useCallback(
        async (
            clientId: number,
            templateId: number,
            stepId: number,
            ruleSetId: number,
            ruleId: number,
            dto: UpdateRuleRequestDto,
        ): Promise<RuleAdminListItemDto> => {
            return api.put<RuleAdminListItemDto>(
                `${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets/${ruleSetId}/rules/${ruleId}`,
                dto,
            );
        },
        [api],
    );

    /** Delete a rule */
    const deleteRule = useCallback(
        async (clientId: number, templateId: number, stepId: number, ruleSetId: number, ruleId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/workflows/${templateId}/steps/${stepId}/rulesets/${ruleSetId}/rules/${ruleId}`);
        },
        [api],
    );

    // =========================================================================
    // PROMPT TEMPLATES
    // =========================================================================

    /** List prompt templates for a client (includes system defaults) */
    const listPromptTemplates = useCallback(
        async (clientId: number, pageSize?: number, skip?: number): Promise<AdminPromptTemplateListResponse> => {
            const params = new URLSearchParams();
            if (pageSize !== undefined) params.set("pageSize", String(pageSize));
            if (skip !== undefined) params.set("skip", String(skip));
            const query = params.toString();
            return api.get<AdminPromptTemplateListResponse>(`${ADMIN_BASE}/clients/${clientId}/prompts${query ? `?${query}` : ""}`);
        },
        [api],
    );

    /** Get a single prompt template with full text content */
    const getPromptTemplate = useCallback(
        async (clientId: number, promptId: number): Promise<PromptTemplateAdminDetailDto> => {
            return api.get<PromptTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/prompts/${promptId}`);
        },
        [api],
    );

    /** Create a new client-specific prompt template */
    const createPromptTemplate = useCallback(
        async (clientId: number, dto: CreatePromptTemplateRequestDto): Promise<PromptTemplateAdminDetailDto> => {
            return api.post<PromptTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/prompts`, dto);
        },
        [api],
    );

    /** Update an existing prompt template */
    const updatePromptTemplate = useCallback(
        async (clientId: number, promptId: number, dto: UpdatePromptTemplateRequestDto): Promise<PromptTemplateAdminDetailDto> => {
            return api.put<PromptTemplateAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/prompts/${promptId}`, dto);
        },
        [api],
    );

    // =========================================================================
    // BRIDGE REGISTRATIONS
    // =========================================================================

    /** List bridge registrations for a client */
    const listBridgeRegistrations = useCallback(
        async (clientId: number): Promise<AdminBridgeRegistrationListResponse> => {
            return api.get<AdminBridgeRegistrationListResponse>(`${ADMIN_BASE}/clients/${clientId}/bridge`);
        },
        [api],
    );

    /** List all bridge registrations across all clients (global bridge health panel) */
    const listAllBridges = useCallback(async (): Promise<AdminGlobalBridgeListResponse> => {
        return api.get<AdminGlobalBridgeListResponse>(`${ADMIN_BASE}/bridges`);
    }, [api]);

    /** Create a new bridge registration for a client */
    const createBridgeRegistration = useCallback(
        async (clientId: number, dto: CreateBridgeRegistrationRequestDto): Promise<BridgeRegistrationAdminListItemDto> => {
            return api.post<BridgeRegistrationAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/bridge`, dto);
        },
        [api],
    );

    /** Update bridge registration config JSON */
    const updateBridgeRegistrationConfig = useCallback(
        async (clientId: number, registrationId: number, dto: UpdateBridgeRegistrationConfigRequestDto): Promise<BridgeRegistrationAdminListItemDto> => {
            return api.put<BridgeRegistrationAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/bridge/${registrationId}`, dto);
        },
        [api],
    );

    /** Regenerate the bridge secret for a client. Returns the new plaintext secret (shown once). */
    const regenerateBridgeSecret = useCallback(
        async (clientId: number): Promise<RegenerateBridgeSecretResponseDto> => {
            return api.post<RegenerateBridgeSecretResponseDto>(`${ADMIN_BASE}/clients/${clientId}/bridge/regenerate-secret`, {});
        },
        [api],
    );

    // =========================================================================
    // USERS
    // =========================================================================

    /** List client users with org memberships */
    const listClientUsers = useCallback(
        async (clientId: number, params?: AdminUserListParams): Promise<AdminUserListResponse> => {
            const searchParams = new URLSearchParams();
            if (params?.isActive !== undefined) searchParams.set("isActive", String(params.isActive));
            if (params?.search) searchParams.set("search", params.search);
            if (params?.pageSize !== undefined) searchParams.set("pageSize", String(params.pageSize));
            if (params?.skip !== undefined) searchParams.set("skip", String(params.skip));
            const query = searchParams.toString();
            return api.get<AdminUserListResponse>(`${ADMIN_BASE}/clients/${clientId}/users${query ? `?${query}` : ""}`);
        },
        [api],
    );

    /** List organizations for a client */
    const listClientOrganizations = useCallback(
        async (clientId: number): Promise<OrganizationAdminListItemDto[]> => {
            const res = await api.get<AdminOrganizationListResponse>(`${ADMIN_BASE}/clients/${clientId}/organizations`);
            return res.data;
        },
        [api],
    );

    /** Add a user to an organization */
    const addUserOrganization = useCallback(
        async (clientId: number, userId: number, organizationId: number): Promise<void> => {
            await api.post(`${ADMIN_BASE}/clients/${clientId}/users/${userId}/organizations`, { organizationId });
        },
        [api],
    );

    /** Remove a user from an organization */
    const removeUserOrganization = useCallback(
        async (clientId: number, userId: number, orgId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/users/${userId}/organizations/${orgId}`);
        },
        [api],
    );

    /** Pull users from Clerk organization matching the client code and upsert them */
    const pullUsersFromClerk = useCallback(
        async (clientId: number): Promise<{ imported: number; skipped: number }> => {
            return api.post<{ imported: number; skipped: number }>(`${ADMIN_BASE}/clients/${clientId}/users/pull-from-clerk`);
        },
        [api],
    );

    /** Update a user (e.g. targetSystemUserId) */
    const updateUser = useCallback(
        async (clientId: number, userId: number, dto: UpdateUserRequestDto): Promise<UserAdminListItemDto> => {
            return api.patch<UserAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/users/${userId}`, dto);
        },
        [api],
    );

    /** Bulk-set the "Private workspace" restriction for all users of a client */
    const bulkUpdateUserRestriction = useCallback(
        async (clientId: number, restrictToOwnDocuments: boolean): Promise<BulkUpdateUserRestrictionResultDto> => {
            return api.patch<BulkUpdateUserRestrictionResultDto>(`${ADMIN_BASE}/clients/${clientId}/users/restrict-all`, {
                restrictToOwnDocuments,
            });
        },
        [api],
    );

    /** Create a new organization for a client */
    const createClientOrganization = useCallback(
        async (clientId: number, dto: CreateOrganizationRequestDto): Promise<OrganizationAdminListItemDto> => {
            const res = await api.post<OrganizationAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/organizations`, dto);
            return res;
        },
        [api],
    );

    /** Update an existing organization */
    const updateClientOrganization = useCallback(
        async (clientId: number, orgId: number, dto: UpdateOrganizationRequestDto): Promise<OrganizationAdminListItemDto> => {
            const res = await api.put<OrganizationAdminListItemDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${orgId}`, dto);
            return res;
        },
        [api],
    );

    /** Deactivate an organization (two-step: pre-check without force, execute with force) */
    const deactivateClientOrganization = useCallback(
        async (clientId: number, orgId: number, force: boolean): Promise<OrgDeactivatePreCheckResponseDto> => {
            const res = await api.delete<OrgDeactivatePreCheckResponseDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${orgId}?force=${force}`);
            return res;
        },
        [api],
    );

    /** List exceptions for a specific client */
    const listClientExceptions = useCallback(
        async (clientId: number, params?: AdminExceptionListParams): Promise<AdminExceptionListResponse> => {
            const searchParams = new URLSearchParams();
            if (params?.status) searchParams.set("status", params.status);
            if (params?.severity) searchParams.set("severity", params.severity);
            if (params?.code) searchParams.set("code", params.code);
            if (params?.startDate) searchParams.set("startDate", params.startDate);
            if (params?.endDate) searchParams.set("endDate", params.endDate);
            if (params?.pageSize !== undefined) searchParams.set("pageSize", String(params.pageSize));
            if (params?.skip !== undefined) searchParams.set("skip", String(params.skip));
            if (params?.source) searchParams.set("source", params.source);
            const query = searchParams.toString();
            const res = await api.get<AdminExceptionListResponse>(`${ADMIN_BASE}/clients/${clientId}/exceptions${query ? `?${query}` : ""}`);
            return res;
        },
        [api],
    );

    /** Get a single exception for a specific client */
    const getClientException = useCallback(
        async (clientId: number, exceptionId: number): Promise<ExceptionAdminDetailDto> => {
            const res = await api.get<ExceptionAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/exceptions/${exceptionId}`);
            return res;
        },
        [api],
    );

    /** Resolve an exception for a specific client */
    const resolveClientException = useCallback(
        async (clientId: number, exceptionId: number, dto: ResolveExceptionRequestDto): Promise<void> => {
            await api.post(`${ADMIN_BASE}/clients/${clientId}/exceptions/${exceptionId}/resolve`, dto);
        },
        [api],
    );

    /** Suppress an exception for a specific client */
    const suppressClientException = useCallback(
        async (clientId: number, exceptionId: number, dto: SuppressExceptionRequestDto): Promise<void> => {
            await api.post(`${ADMIN_BASE}/clients/${clientId}/exceptions/${exceptionId}/suppress`, dto);
        },
        [api],
    );

    /** Reopen an exception for a specific client */
    const reopenClientException = useCallback(
        async (clientId: number, exceptionId: number): Promise<void> => {
            await api.post(`${ADMIN_BASE}/clients/${clientId}/exceptions/${exceptionId}/reopen`, {});
        },
        [api],
    );

    /** List audit events for a specific client */
    const listClientAuditEvents = useCallback(
        async (clientId: number, params?: AdminAuditEventListParams): Promise<AdminAuditEventListResponse> => {
            const searchParams = new URLSearchParams();
            if (params?.action) searchParams.set("action", params.action);
            if (params?.entityType) searchParams.set("entityType", params.entityType);
            if (params?.entityId !== undefined) searchParams.set("entityId", String(params.entityId));
            if (params?.actorType) searchParams.set("actorType", params.actorType);
            if (params?.startDate) searchParams.set("startDate", params.startDate);
            if (params?.endDate) searchParams.set("endDate", params.endDate);
            if (params?.pageSize !== undefined) searchParams.set("pageSize", String(params.pageSize));
            if (params?.skip !== undefined) searchParams.set("skip", String(params.skip));
            const query = searchParams.toString();
            const res = await api.get<AdminAuditEventListResponse>(`${ADMIN_BASE}/clients/${clientId}/audit-events${query ? `?${query}` : ""}`);
            return res;
        },
        [api],
    );

    /** Get a single audit event (including old/new values JSON) for a specific client */
    const getClientAuditEvent = useCallback(
        async (clientId: number, auditEventId: number): Promise<AuditEventAdminDetailDto> => {
            const res = await api.get<AuditEventAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/audit-events/${auditEventId}`);
            return res;
        },
        [api],
    );

    /** List jobs for a specific client */
    const listClientJobs = useCallback(
        async (clientId: number, params?: AdminJobListParams): Promise<AdminJobListResponse> => {
            const searchParams = new URLSearchParams();
            if (params?.jobType) searchParams.set("jobType", params.jobType);
            if (params?.status) searchParams.set("status", params.status);
            if (params?.startDate) searchParams.set("startDate", params.startDate);
            if (params?.endDate) searchParams.set("endDate", params.endDate);
            if (params?.pageSize !== undefined) searchParams.set("pageSize", String(params.pageSize));
            if (params?.skip !== undefined) searchParams.set("skip", String(params.skip));
            const query = searchParams.toString();
            const res = await api.get<AdminJobListResponse>(`${ADMIN_BASE}/clients/${clientId}/jobs${query ? `?${query}` : ""}`);
            return res;
        },
        [api],
    );

    /** Get a single job for a specific client */
    const getClientJob = useCallback(
        async (clientId: number, jobId: number): Promise<JobAdminDetailDto> => {
            const res = await api.get<JobAdminDetailDto>(`${ADMIN_BASE}/clients/${clientId}/jobs/${jobId}`);
            return res;
        },
        [api],
    );

    /** Cancel a queued job for a specific client */
    const cancelClientJob = useCallback(
        async (clientId: number, jobId: number): Promise<void> => {
            await api.post(`${ADMIN_BASE}/clients/${clientId}/jobs/${jobId}/cancel`, {});
        },
        [api],
    );

    /** Requeue a failed or canceled job for a specific client */
    const requeueClientJob = useCallback(
        async (clientId: number, jobId: number): Promise<void> => {
            await api.post(`${ADMIN_BASE}/clients/${clientId}/jobs/${jobId}/requeue`, {});
        },
        [api],
    );

    // =========================================================================
    // MONITORING DASHBOARD
    // =========================================================================

    /** Get aggregated monitoring dashboard for a client */
    const getMonitoringDashboard = useCallback(
        async (clientId: number, windowMinutes: number = 60): Promise<MonitoringDashboardDto> => {
            const params = new URLSearchParams();
            params.set("windowMinutes", String(windowMinutes));
            return api.get<MonitoringDashboardDto>(`${ADMIN_BASE}/clients/${clientId}/monitoring/dashboard?${params.toString()}`);
        },
        [api],
    );

    /** Bulk requeue failed/canceled jobs for a client */
    const bulkRequeueClientJobs = useCallback(
        async (clientId: number, request: BulkRequeueRequestDto): Promise<BulkRequeueResponseDto> => {
            return api.post<BulkRequeueResponseDto>(`${ADMIN_BASE}/clients/${clientId}/jobs/requeue-batch`, request);
        },
        [api],
    );

    // =========================================================================
    // ORGANIZATION DOCUMENT TYPES
    // =========================================================================

    /** List document types assigned to an organization */
    const listOrganizationDocumentTypes = useCallback(
        async (clientId: number, organizationId: number): Promise<OrganizationDocumentTypeDto[]> => {
            const res = await api.get<AdminOrganizationDocumentTypeListResponse>(
                `${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/document-types`,
            );
            return res.data;
        },
        [api],
    );

    /** Add a document type to an organization (may return warnings for duplicate codes) */
    const addOrganizationDocumentType = useCallback(
        async (clientId: number, organizationId: number, dto: AddOrganizationDocumentTypeRequestDto): Promise<AddOrganizationDocumentTypeResponseDto> => {
            return api.post<AddOrganizationDocumentTypeResponseDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/document-types`, dto);
        },
        [api],
    );

    /** Update IsInbound on an organization's document type assignment */
    const updateOrganizationDocumentType = useCallback(
        async (
            clientId: number,
            organizationId: number,
            documentTypeId: number,
            dto: UpdateOrganizationDocumentTypeRequestDto,
        ): Promise<OrganizationDocumentTypeDto> => {
            return api.patch<OrganizationDocumentTypeDto>(
                `${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/document-types/${documentTypeId}`,
                dto,
            );
        },
        [api],
    );

    /** Remove a document type from an organization */
    const removeOrganizationDocumentType = useCallback(
        async (clientId: number, organizationId: number, documentTypeId: number): Promise<void> => {
            await api.delete(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/document-types/${documentTypeId}`);
        },
        [api],
    );

    // =========================================================================
    // ORGANIZATION FILE TYPES
    // =========================================================================

    /** List file type configuration rows for an organization (absent row = disabled) */
    const listOrganizationFileTypes = useCallback(
        async (clientId: number, organizationId: number): Promise<OrganizationFileTypeDto[]> => {
            const res = await api.get<AdminOrganizationFileTypeListResponse>(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/file-types`);
            return res.data;
        },
        [api],
    );

    /** Set an organization's file type toggle (upsert — creates the row when missing) */
    const setOrganizationFileType = useCallback(
        async (clientId: number, organizationId: number, fileType: number, dto: SetOrganizationFileTypeRequestDto): Promise<OrganizationFileTypeDto> => {
            return api.put<OrganizationFileTypeDto>(`${ADMIN_BASE}/clients/${clientId}/organizations/${organizationId}/file-types/${fileType}`, dto);
        },
        [api],
    );

    // =========================================================================
    // EMAIL SOURCES
    // =========================================================================

    /** List email source configurations for a client */
    const listEmailSources = useCallback(
        async (clientId: number): Promise<AdminEmailSourceListResponse> =>
            api.get<AdminEmailSourceListResponse>(`${ADMIN_BASE}/clients/${clientId}/email-sources`),
        [api],
    );

    /** Get a single email source configuration */
    const getEmailSource = useCallback(
        async (clientId: number, id: number): Promise<MailboxConfigurationDto> =>
            api.get<MailboxConfigurationDto>(`${ADMIN_BASE}/clients/${clientId}/email-sources/${id}`),
        [api],
    );

    /** Create an email source configuration */
    const createEmailSource = useCallback(
        async (clientId: number, dto: CreateMailboxConfigurationDto): Promise<MailboxConfigurationDto> =>
            api.post<MailboxConfigurationDto>(`${ADMIN_BASE}/clients/${clientId}/email-sources`, dto),
        [api],
    );

    /** Update an email source configuration */
    const updateEmailSource = useCallback(
        async (clientId: number, id: number, dto: UpdateMailboxConfigurationDto): Promise<MailboxConfigurationDto> =>
            api.put<MailboxConfigurationDto>(`${ADMIN_BASE}/clients/${clientId}/email-sources/${id}`, dto),
        [api],
    );

    /** Delete an email source configuration */
    const deleteEmailSource = useCallback(
        async (clientId: number, id: number): Promise<void> => api.delete(`${ADMIN_BASE}/clients/${clientId}/email-sources/${id}`),
        [api],
    );

    /** Get live connection status for an email source */
    const getEmailSourceStatus = useCallback(
        async (clientId: number, id: number): Promise<MailboxStatusDto> =>
            api.get<MailboxStatusDto>(`${ADMIN_BASE}/clients/${clientId}/email-sources/${id}/status`),
        [api],
    );

    /** Get OAuth authorization URL to connect a mailbox */
    const getEmailSourceConnectUrl = useCallback(
        async (clientId: number, id: number): Promise<string> => {
            const res = await api.get<{ authUrl: string }>(`${ADMIN_BASE}/clients/${clientId}/email-sources/${id}/connect`);
            return res.authUrl;
        },
        [api],
    );

    /** Disconnect (revoke tokens) for an email source */
    const disconnectEmailSource = useCallback(
        async (clientId: number, id: number): Promise<void> => api.post(`${ADMIN_BASE}/clients/${clientId}/email-sources/${id}/disconnect`, {}),
        [api],
    );

    // =========================================================================
    // MAILBOX WORKER API KEY
    // =========================================================================

    /** Get global mailbox worker API key metadata (prefix + generatedAt, never plaintext) */
    const getMailboxWorkerKey = useCallback(
        async (): Promise<MailboxWorkerKeyMetadataDto> => api.get<MailboxWorkerKeyMetadataDto>(`${ADMIN_BASE}/mailbox-worker-key`),
        [api],
    );

    /** Generate a new global mailbox worker API key (returns plaintext once) */
    const generateMailboxWorkerKey = useCallback(
        async (): Promise<MailboxWorkerKeyGeneratedDto> => api.post<MailboxWorkerKeyGeneratedDto>(`${ADMIN_BASE}/mailbox-worker-key`, {}),
        [api],
    );

    /** Revoke the global mailbox worker API key */
    const revokeMailboxWorkerKey = useCallback(async (): Promise<void> => api.delete(`${ADMIN_BASE}/mailbox-worker-key`), [api]);

    return useMemo(
        () => ({
            // Clients
            listClients,
            getClient,
            createClient,
            updateClient,
            getClientSettings,
            updateClientSettings,
            getClientUsage,
            getEnumMetadata,
            // Plans
            listPlans,
            createPlan,
            updatePlan,
            getPlanEntitlements,
            updatePlanEntitlement,
            listClientPlans,
            createClientPlan,
            updateClientPlan,
            listClientPlanEntitlements,
            // Entitlements
            listEntitlements,
            listClientEntitlements,
            createClientEntitlement,
            updateClientEntitlement,
            deleteClientEntitlement,
            // Fields
            listFieldDefinitions,
            getFieldDefinition,
            createFieldDefinition,
            updateFieldDefinition,
            deleteFieldDefinition,
            reorderFieldDefinitions,
            // ACOM defaults
            getAcomDefaults,
            // Reference data
            listDocumentTypes,
            createDocumentType,
            updateDocumentType,
            deleteDocumentType,
            listLineItemTypes,
            createLineItemType,
            updateLineItemType,
            // Users & Organizations
            listClientUsers,
            pullUsersFromClerk,
            updateUser,
            bulkUpdateUserRestriction,
            listClientOrganizations,
            addUserOrganization,
            removeUserOrganization,
            createClientOrganization,
            updateClientOrganization,
            deactivateClientOrganization,
            // Step types
            listStepTypes,
            // Workflows
            listWorkflowTemplates,
            getWorkflowTemplate,
            createWorkflowTemplate,
            updateWorkflowTemplate,
            deleteWorkflowTemplate,
            // Workflow steps
            createWorkflowStep,
            updateWorkflowStep,
            deleteWorkflowStep,
            reorderWorkflowSteps,
            // Rule sets
            listStepRuleSets,
            createRuleSet,
            updateRuleSet,
            deleteRuleSet,
            // Rules
            createRule,
            updateRule,
            deleteRule,
            // Prompt Templates
            listPromptTemplates,
            getPromptTemplate,
            createPromptTemplate,
            updatePromptTemplate,
            // Bridge Registrations
            listAllBridges,
            listBridgeRegistrations,
            createBridgeRegistration,
            updateBridgeRegistrationConfig,
            regenerateBridgeSecret,
            // Exceptions
            listClientExceptions,
            getClientException,
            resolveClientException,
            suppressClientException,
            reopenClientException,
            // Audit Events
            listClientAuditEvents,
            getClientAuditEvent,
            // Jobs
            listClientJobs,
            getClientJob,
            cancelClientJob,
            requeueClientJob,
            // Monitoring
            getMonitoringDashboard,
            bulkRequeueClientJobs,
            // Organization Document Types
            listOrganizationDocumentTypes,
            addOrganizationDocumentType,
            updateOrganizationDocumentType,
            removeOrganizationDocumentType,
            // Organization File Types
            listOrganizationFileTypes,
            setOrganizationFileType,
            // Email Sources
            listEmailSources,
            getEmailSource,
            createEmailSource,
            updateEmailSource,
            deleteEmailSource,
            getEmailSourceStatus,
            getEmailSourceConnectUrl,
            disconnectEmailSource,
            // Mailbox Worker API Key
            getMailboxWorkerKey,
            generateMailboxWorkerKey,
            revokeMailboxWorkerKey,
        }),
        [
            listClients,
            getClient,
            createClient,
            updateClient,
            getClientSettings,
            updateClientSettings,
            getClientUsage,
            getEnumMetadata,
            getAcomDefaults,
            listPlans,
            createPlan,
            updatePlan,
            getPlanEntitlements,
            updatePlanEntitlement,
            listClientPlans,
            createClientPlan,
            updateClientPlan,
            listClientPlanEntitlements,
            listEntitlements,
            listClientEntitlements,
            createClientEntitlement,
            updateClientEntitlement,
            deleteClientEntitlement,
            listFieldDefinitions,
            getFieldDefinition,
            createFieldDefinition,
            updateFieldDefinition,
            deleteFieldDefinition,
            reorderFieldDefinitions,
            listDocumentTypes,
            createDocumentType,
            updateDocumentType,
            deleteDocumentType,
            listLineItemTypes,
            createLineItemType,
            updateLineItemType,
            listClientUsers,
            listClientOrganizations,
            addUserOrganization,
            removeUserOrganization,
            createClientOrganization,
            updateClientOrganization,
            deactivateClientOrganization,
            listStepTypes,
            listWorkflowTemplates,
            getWorkflowTemplate,
            createWorkflowTemplate,
            updateWorkflowTemplate,
            deleteWorkflowTemplate,
            createWorkflowStep,
            updateWorkflowStep,
            deleteWorkflowStep,
            reorderWorkflowSteps,
            listStepRuleSets,
            createRuleSet,
            updateRuleSet,
            deleteRuleSet,
            createRule,
            updateRule,
            deleteRule,
            listPromptTemplates,
            getPromptTemplate,
            createPromptTemplate,
            updatePromptTemplate,
            listAllBridges,
            listBridgeRegistrations,
            createBridgeRegistration,
            updateBridgeRegistrationConfig,
            regenerateBridgeSecret,
            listClientExceptions,
            getClientException,
            resolveClientException,
            suppressClientException,
            reopenClientException,
            listClientAuditEvents,
            getClientAuditEvent,
            listClientJobs,
            getClientJob,
            cancelClientJob,
            requeueClientJob,
            getMonitoringDashboard,
            bulkRequeueClientJobs,
            listOrganizationDocumentTypes,
            addOrganizationDocumentType,
            updateOrganizationDocumentType,
            removeOrganizationDocumentType,
            listOrganizationFileTypes,
            setOrganizationFileType,
            listEmailSources,
            getEmailSource,
            createEmailSource,
            updateEmailSource,
            deleteEmailSource,
            getEmailSourceStatus,
            getEmailSourceConnectUrl,
            disconnectEmailSource,
            getMailboxWorkerKey,
            generateMailboxWorkerKey,
            revokeMailboxWorkerKey,
        ],
    );
}

/** Type of the admin API hook return value */
export type AdminApi = ReturnType<typeof useAdminApi>;
